import React, { memo } from 'react';
import { Button, Chip, Spinner } from '@heroui/react';
import { 
  UsersThree, 
  EnvelopeSimple, 
  Phone, 
  Building, 
  Warning,
  XCircle,
  Plus,
  Eye,
  EyeSlash
} from '@phosphor-icons/react';
import { useMassMailContext } from '../context/MassMailContext';
import { RecipientTypeSelect } from './RecipientTypeSelect';
import { hasUsableEmail, resolveRecipientTypes } from '../utils/recipientTypes';
import { Contact, ContactUnit, RecipientType } from '../types';

interface ContactsListProps {}

interface UnitChipDescriptor {
  unitId: string;
  boxNumber: string;
  referencePlus: string;
  buildingId: string;
  buildingName: string;
}

const buildUnitChips = (contact: Contact): UnitChipDescriptor[] => {
  const sourceUnits: ContactUnit[] = contact.units && contact.units.length > 0
    ? contact.units
    : contact.buildingId
      ? [{
          id: contact.unitId || '',
          unitId: contact.unitId || '',
          unitNumber: contact.unitNumber || '',
          referencePlus: '',
          buildingId: contact.buildingId,
          buildingName: contact.buildingName || '',
        }]
      : [];

  return sourceUnits.map((u) => ({
    unitId: (u.unitId || '').trim(),
    boxNumber: (u.unitNumber || '').trim(),
    referencePlus: (u.referencePlus || '').trim(),
    buildingId: u.buildingId || '',
    buildingName: u.buildingName || '',
  }));
};

interface UnitDetailLineProps {
  unit: UnitChipDescriptor;
}

const splitUnitIdByBuilding = (unitId: string, buildingId: string): { shared: string; distinctive: string } => {
  if (!unitId) return { shared: '', distinctive: '' };
  if (buildingId && unitId.startsWith(buildingId)) {
    return { shared: buildingId, distinctive: unitId.slice(buildingId.length) };
  }
  return { shared: '', distinctive: unitId };
};

const DetailLabel: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <span className="inline-flex items-center gap-1 rounded-md border border-neutral-secondary-mid/25 bg-neutral-secondary-mid/5 px-1.5 py-0.5 text-[11px] text-neutral-secondary-mid">
    <span className="font-medium uppercase tracking-wide">{label}</span>
    <span className="text-neutral-primary">{children}</span>
  </span>
);

const UnitDetailLine: React.FC<UnitDetailLineProps> = ({ unit }) => {
  const hasUnitId = !!unit.unitId;
  const showBox = !!unit.boxNumber && unit.boxNumber !== unit.unitId;
  const showRef = !!unit.referencePlus;

  if (!hasUnitId && !showBox && !showRef) return null;

  const { shared, distinctive } = splitUnitIdByBuilding(unit.unitId, unit.buildingId);

  return (
    <div className="flex flex-wrap items-center gap-1 mt-1 ml-0.5">
      {hasUnitId && (
        <DetailLabel label="Unit ID">
          {shared && <span className="text-neutral-secondary-mid">{shared}</span>}
          {distinctive && <span className="font-semibold text-brick-primary">{distinctive}</span>}
        </DetailLabel>
      )}
      {showBox && <DetailLabel label="Bus">{unit.boxNumber}</DetailLabel>}
      {showRef && <DetailLabel label="Ref+">{unit.referencePlus}</DetailLabel>}
    </div>
  );
};

export const ContactsList: React.FC<ContactsListProps> = memo(() => {
  const { state, dispatch } = useMassMailContext();
  const [showExcluded, setShowExcluded] = React.useState(false);

  const handleRemoveContact = (contactId: string) => {
    dispatch({ type: 'REMOVE_CONTACT', payload: contactId });
  };

  const handleRestoreContact = (contact: Contact) => {
    dispatch({ type: 'RESTORE_CONTACT', payload: contact });
  };

  const handleRecipientTypeChange = (contactId: string, recipientType: RecipientType) => {
    dispatch({ type: 'SET_CONTACT_RECIPIENT_TYPE', payload: { contactId, recipientType } });
  };

  // Check if contacts were pre-populated (no selected resources but contacts exist)
  const hasPrePopulatedContacts = state.contacts.length > 0 && state.selectedResources.length === 0;

  // Check if contact has valid email — gedeeld met de verzendlogica
  const hasValidEmail = (contact: Contact): boolean => hasUsableEmail(contact);

  // Generate consistent color for building
  const getBuildingColor = (contact: Contact): string => {
    const buildingId = contact.buildingId || (contact.units.length > 0 ? contact.units[0].buildingId : undefined);
    
    if (!buildingId) return 'bg-gray-100 border-gray-300 text-gray-700';
    
    let hash = 0;
    for (let i = 0; i < buildingId.length; i++) {
      hash = buildingId.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const colors = [
      'bg-neutral-primary/5 border-neutral-primary/15 text-neutral-primary',
      'bg-moss-primary/8 border-moss-primary/20 text-moss-primary',
      'bg-neutral-secondary-mid/10 border-neutral-secondary-mid/25 text-neutral-secondary-mid',
      'bg-status-good/8 border-status-good/20 text-status-good',
      'bg-blue-100/60 border-blue-200/50 text-blue-700',
      'bg-purple-100/60 border-purple-200/50 text-purple-600',
      'bg-cyan-100/50 border-cyan-200/60 text-cyan-700',
      'bg-emerald-100/50 border-emerald-200/60 text-emerald-700'
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  const validEmailCount = state.contacts.filter(hasValidEmail).length;

  // Dezelfde afleiding als de verzendfunctie gebruikt, zodat wat hier "Aan" toont ook
  // echt in het TO-veld belandt.
  const recipientTypes = resolveRecipientTypes(state.contacts);

  // Iedereen in CC/BCC is toegestaan; de gebruiker moet dat wel kunnen zien.
  const heeftGeenToOntvanger =
    state.enableRecipientTypes &&
    validEmailCount > 0 &&
    !state.contacts.some((contact, index) => hasValidEmail(contact) && recipientTypes[index] === 'to');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Contacten</h3>
        <div className="flex items-center gap-2">
          {hasPrePopulatedContacts && (
            <Chip size="sm" className="bg-brick-primary/10 text-brick-primary border border-brick-primary/30">
              Vooraf geselecteerd
            </Chip>
          )}
          <Chip size="sm" variant="flat" color="primary">
            {state.contacts.length} totaal
          </Chip>
          <Chip size="sm" variant="flat" color="success">
            {validEmailCount} met email
          </Chip>
          {state.excludedContactIds.size > 0 && (
            <Chip size="sm" variant="flat" color="warning">
              {state.excludedContactIds.size} uitgesloten
            </Chip>
          )}
        </div>
      </div>

      {/* Uitleg bij de Aan/CC/BCC-keuze: één mail voor alle ontvangers samen */}
      {state.enableRecipientTypes && state.contacts.length > 0 && (
        <p className="text-xs text-neutral-secondary-mid">
          Alle ontvangers krijgen dezelfde mail. Kies per contact of die in &ldquo;Aan&rdquo;, CC of BCC komt —
          er kan hoogstens één contact in &ldquo;Aan&rdquo; staan.
        </p>
      )}

      {/* Alles in BCC mag, maar mag geen verrassing zijn */}
      {heeftGeenToOntvanger && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <EnvelopeSimple size={16} className="mt-0.5 shrink-0 text-blue-600" />
          <p className="text-sm text-blue-800">
            Niemand staat in &ldquo;Aan&rdquo;. De mail vertrekt met alleen CC- en BCC-ontvangers, zodat de
            BCC-adressen voor niemand zichtbaar zijn.
          </p>
        </div>
      )}

      {/* Toggle excluded contacts button */}
      {state.excludedContacts.length > 0 && (
        <Button
          size="sm"
          variant={showExcluded ? "solid" : "bordered"}
          color={showExcluded ? "warning" : "default"}
          startContent={showExcluded ? <EyeSlash size={14} /> : <Eye size={14} />}
          onPress={() => setShowExcluded(!showExcluded)}
        >
          {showExcluded ? 'Verberg uitgesloten' : 'Toon uitgesloten'}
        </Button>
      )}

      {/* Contacts List */}
      <div className="space-y-2">
        {state.isLoadingContacts ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="sm" />
            <span className="ml-2 text-sm text-gray-500">Laden...</span>
          </div>
        ) : !showExcluded && state.contacts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <UsersThree size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">Geen contacten gevonden</p>
            {state.selectedResources.length === 0 && (
              <p className="text-xs mt-1">Selecteer eerst resources</p>
            )}
          </div>
        ) : showExcluded ? (
          // Show excluded contacts
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={16} className="text-amber-500" />
              <span className="text-sm font-medium text-amber-700">
                Uitgesloten contacten ({state.excludedContacts.length})
              </span>
            </div>
            {state.excludedContacts.map((contact) => (
              <div
                key={contact.id}
                className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200 hover:border-amber-300 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-700 truncate">
                        {contact.name}
                      </p>
                      {!hasValidEmail(contact) && (
                        <Warning size={14} className="text-amber-500 shrink-0" />
                      )}
                    </div>
                    
                    {contact.email && hasValidEmail(contact) && (
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <EnvelopeSimple size={12} />
                        <span className="truncate">{contact.email}</span>
                      </div>
                    )}
                    
                    {contact.phoneMobile && (
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <Phone size={12} />
                        <span>{contact.phoneMobile}</span>
                      </div>
                    )}

                    {/* Building/Unit info */}
                    <div className="flex flex-col gap-1 mt-1">
                      {buildUnitChips(contact).map((unit, idx) => (
                        <div key={`${unit.unitId || unit.buildingId}-${idx}`} className="flex flex-col">
                          {unit.buildingName && (
                            <Chip
                              size="sm"
                              variant="bordered"
                              className={`text-xs self-start ${getBuildingColor(contact)}`}
                              startContent={<Building size={10} />}
                            >
                              {unit.buildingName}
                            </Chip>
                          )}
                          <UnitDetailLine unit={unit} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Restore Contact Button */}
                <Button
                  size="sm"
                  color="success"
                  variant="flat"
                  startContent={<Plus size={14} />}
                  onPress={() => handleRestoreContact(contact)}
                  title="Voeg terug toe aan lijst"
                >
                  Toevoegen
                </Button>
              </div>
            ))}
          </div>
        ) : (
          // Show active contacts
          state.contacts.map((contact, index) => (
            <div
              key={contact.id}
              className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-gray-900 truncate">
                      {contact.name}
                    </p>
                    {!hasValidEmail(contact) && (
                      <Warning size={14} className="text-amber-500 shrink-0" />
                    )}
                  </div>
                  
                  {contact.email && hasValidEmail(contact) && (
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <EnvelopeSimple size={12} />
                      <span className="truncate">{contact.email}</span>
                    </div>
                  )}
                  
                  {contact.phoneMobile && (
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <Phone size={12} />
                      <span>{contact.phoneMobile}</span>
                    </div>
                  )}

                  {/* Building/Unit info */}
                  <div className="flex flex-col gap-1 mt-1">
                    {buildUnitChips(contact).map((unit, idx) => (
                      <div key={`${unit.unitId || unit.buildingId}-${idx}`} className="flex flex-col">
                        {unit.buildingName && (
                          <Chip
                            size="sm"
                            variant="bordered"
                            className={`text-xs self-start ${getBuildingColor(contact)}`}
                            startContent={<Building size={10} />}
                          >
                            {unit.buildingName}
                          </Chip>
                        )}
                        <UnitDetailLine unit={unit} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Aan/CC/BCC-keuze per contact */}
                {state.enableRecipientTypes && (
                  <RecipientTypeSelect
                    value={recipientTypes[index] ?? 'cc'}
                    contactName={contact.name}
                    isDisabled={!hasValidEmail(contact)}
                    onChange={(recipientType) => handleRecipientTypeChange(contact.id, recipientType)}
                  />
                )}

                {/* Remove Contact Button */}
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="danger"
                  onPress={() => handleRemoveContact(contact.id)}
                  className="shrink-0"
                  title="Verwijder uit lijst"
                >
                  <XCircle size={14} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Warning for contacts without email */}
      {state.contacts.length > validEmailCount && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Warning size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Let op:</p>
              <p>
                {state.contacts.length - validEmailCount} contact{state.contacts.length - validEmailCount !== 1 ? 'en hebben' : ' heeft'} geen geldig e-mailadres en zullen niet worden meegenomen.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
